import { getSerialPortScanner, getSerialPortScale, SerialPortScale } from "@q-food/q-food";
import { useUserStore } from "~stores/User.ts";

const websocket: Ref<WebSocket | undefined> = ref();
let reconnectInterval: number | null = null;
let pingInterval: number | null = null;
let scaleStatusCheck = null;
export const hasBridgeIntegration = ref(false);
let tries = 0;
export const downloadUrl = ref<string | null>(null);
export const currentVersion = ref<string | null>(null);
export const latestVersion = ref<string | null>(null);

let scaleHandler: (() => void) | null = null;
let scannerHandler: (() => void) | null = null;
const lastMessageTime = ref(0);
const lastScannerMessage = ref(0);

// Every open tab registers its own reactive state here. A single websocket
// message handler fans the incoming weight/connection data out to all of them
const scaleStates = new Set<ScaleState>();

const WS_BASE_URL = import.meta.env.VITE_PULSEFOOD_BRIDGE_HOST ?? "ws://localhost:1337/bridge";

export function getWebSocket(): Ref<WebSocket | undefined> {
	return websocket;
}

type ScaleState = {
	isConnected: Ref<boolean>;
	lastMessageTime: Ref<number>;
	currentWeight: Ref<number>;
};

type ScannerState = {
	isConnected: Ref<boolean>;
	lastScannerMessage: Ref<number>;
	valueToUpdate: Ref<string>;
};

export function startWebSocket(): void {
	if (reconnectInterval != null) return; // already running

	attemptConnection();

	// @ts-ignore
	reconnectInterval = setInterval(() => {
		const state = websocket.value?.readyState;

		if (state === undefined || state === WebSocket.CLOSED || state === WebSocket.CLOSING) {
			if (!hasBridgeIntegration.value) tries++;
			attemptConnection();
		} else if (state === WebSocket.OPEN) {
			hasBridgeIntegration.value = true;
			tries = 0;
		}
		// CONNECTING: a connect is already in progress — leave it alone.

		if (tries >= 5 && !hasBridgeIntegration.value && reconnectInterval != null) {
			clearInterval(reconnectInterval);
			reconnectInterval = null;
		}
	}, 2000);
}

export function watchBridgeExpectation(): void {
	const { activeUser } = storeToRefs(useUserStore());

	watch(
		activeUser,
		(user) => {
			const settings = user?.settings;
			if (!settings) return;

			const expectsBridge =
				(settings.defaultScale?.type == "COM" && settings.defaultScale?.id != null) ||
				settings.defaultScannerId != null;
			if (!expectsBridge) return;

			hasBridgeIntegration.value = true;
		},
		{ immediate: true },
	);
}

export function stopWebSocket(): void {
	if (reconnectInterval != null) {
		clearInterval(reconnectInterval);
		reconnectInterval = null;
	}
	stopPing();
	const ws = websocket.value;
	websocket.value = undefined;
	ws?.close();
}

if (import.meta.hot) import.meta.hot.dispose(() => stopWebSocket());

// The browser closes the socket with 1001 ("going away") while the page unloads, after which the
// reconnect timer happily opened a fresh one on a page that is disappearing. That socket completes its
// handshake and is thrown away immediately, but the bridge counts it as another client in the meantime —
// which is what showed up as a second bridge connection on every reload. `persisted` tells a real unload
// apart from the page being put in the back/forward cache, where it may still be restored.
window.addEventListener("pagehide", (event) => {
	if (!event.persisted) stopWebSocket();
});

export const isConnectedToBridge = ref(false);

function parseWSString(str: string) {
	if (str.startsWith("{") && str.endsWith("}")) {
		return JSON.parse(str);
	}
	return str;
}

function createScaleMessageHandler(ws: WebSocket, scalePath: string) {
	const handler = (message) => {
		const wsInput = parseWSString(message.data);

		if (wsInput.address != scalePath) return;

		const connected = !wsInput.error;
		lastMessageTime.value = performance.now();

		clearInterval(scaleStatusCheck);
		scaleStatusCheck = setInterval(() => {
			scaleStates.forEach((state) => (state.isConnected.value = false));
		}, 5000);

		const weight = wsInput.value != null ? Number(wsInput.value.weight) : null;

		// Fan the update out to every open tab, not just the one that opened the connection.
		scaleStates.forEach((state) => {
			state.isConnected.value = connected;
			if (weight != null) state.currentWeight.value = weight;
		});
	};

	ws.addEventListener("message", handler);

	return () => ws?.removeEventListener("message", handler);
}

function createScannerMessageHandler(ws: WebSocket, scannerPath: string, state: ScannerState) {
	const handler = (message) => {
		const wsInput = parseWSString(message.data);
		if (wsInput.address != scannerPath) return;
		state.isConnected.value = !wsInput.error;
		state.lastScannerMessage.value = performance.now();

		// Only forward actual scans. The bridge answers on this same address with status/ack payloads —
		// that is where `error` above comes from — and those used to be pushed into the consumer's value
		// ref as well, where a non-string poisons whatever the consumer does with it (`.trim()`,
		// `.includes()`), losing the scan and breaking the view's update cycle.
		if (typeof wsInput.value === "string" && wsInput.value.trim() !== "") {
			state.valueToUpdate.value = wsInput.value;
		}
	};
	ws.addEventListener("message", handler);

	return () => ws?.removeEventListener("message", handler);
}

watch(websocket, () => {
	isConnectedToBridge.value = websocket.value?.readyState == WebSocket.OPEN;
});

function attemptConnection(): void {
	const existing = websocket.value;
	// Don't pile a new socket on top of a live or in-progress one.
	if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) {
		return;
	}

	const ws = new WebSocket(WS_BASE_URL);
	// Becomes the current socket immediately, so any old socket's late events see themselves as stale.
	websocket.value = ws;
	if (existing) {
		try {
			existing.close();
		} catch {
			// ignore
		}
	}

	ws.addEventListener("open", () => {
		if (ws !== websocket.value) return; // stale socket
		isConnectedToBridge.value = true;
		fetchUpdates().catch((error) => console.warn("Failed to fetch bridge updates:", error));
		startPing();
	});

	ws.addEventListener("close", () => {
		if (ws !== websocket.value) return; // stale socket closing — no log, no churn
		isConnectedToBridge.value = false;
		stopPing();
		console.log("Connection closed, retrying...");
	});

	ws.addEventListener("error", () => {
		ws.close(); // close THIS socket, not whatever is currently assigned
	});
}

function startPing(): void {
	if (pingInterval != null) return;
	// @ts-ignore
	pingInterval = setInterval(() => {
		const ws = websocket.value;
		if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ destination: "ping" }));
	}, 10000);
}

function stopPing(): void {
	if (pingInterval != null) {
		clearInterval(pingInterval);
		pingInterval = null;
	}
}

export async function setActiveScanner(id: string, scannedValue: Ref<string>, isConnected: Ref<boolean>) {
	return await getSerialPortScanner(id).then((response) => {
		const scanner = response;
		const connectionData = JSON.stringify({
			destination: "/serialScanner",
			portData: {
				baudRate: scanner.baudRate,
				numDataBits: scanner.dataBits,
				numStopBits: scanner.stopBits,
				parity: scanner.parity,
				port: scanner.path,
			},
		});
		// The socket the port was configured on, so it is configured once per connection and again after a
		// reconnect. A scanner only sends data when someone scans, so "no message for 2 seconds" says nothing
		// about the connection — unlike the scale, which streams continuously. Re-sending the port
		// configuration on that basis re-opened the serial port every single second, and a scan landing in
		// that window was lost before the client ever saw it.
		let configuredSocket: WebSocket | null = null;
		// The remover for the handler this registration installed, so cleanup only tears down its own.
		let ownHandlerRemover: (() => void) | null = null;

		const setActiveScannerInterval = setInterval(() => {
			const ws = getWebSocket();
			const socketIsOpen = ws.value?.readyState == WebSocket.OPEN;

			if (socketIsOpen && ws.value !== configuredSocket) {
				configuredSocket = ws.value!;
				ws.value!.send(connectionData);
				if (scannerHandler) scannerHandler();
				const state = {
					isConnected,
					lastScannerMessage,
					valueToUpdate: scannedValue,
				};
				ownHandlerRemover = createScannerMessageHandler(ws.value!, scanner.path, state);
				scannerHandler = ownHandlerRemover;
				// A scanner is idle between scans, so its status is "the bridge is up and the port is configured"
				// rather than "a message arrived recently". The handler still lowers it when the bridge reports an
				// error on this port, and it is only raised again by a successful (re)configure.
				isConnected.value = true;
			}

			if (!socketIsOpen) {
				configuredSocket = null;
				isConnected.value = false;
			}

			if (tries >= 3 && !hasBridgeIntegration.value) {
				clearInterval(setActiveScannerInterval);
			}
		}, 1000);

		// Cleanup for when the consuming view goes away. Without this every mount left another interval
		// running that kept re-installing a handler bound to its own (by then unmounted) value ref, so the
		// live view stopped receiving scans.
		return () => {
			clearInterval(setActiveScannerInterval);
			if (scannerHandler === ownHandlerRemover) {
				scannerHandler?.();
				scannerHandler = null;
			}
			ownHandlerRemover = null;
			configuredSocket = null;
		};
	});
}

export async function setActiveSerialPortScale(
	defaultScale: Ref<SerialPortScale>,
	scale: Ref<SerialPortScale>,
	isConnected: Ref<boolean>,
	currentWeight: Ref<number>,
) {
	return await getSerialPortScale(defaultScale.value!.id).then((response) => {
		scale.value = response;
		scale.value.type = "COM";
		scale.value.automatic = defaultScale.value?.automatic ?? false;
		const connectionData = JSON.stringify({
			destination: "/serialScale",
			protocol: scale.value.protocol,
			portData: {
				baudRate: scale.value.baudRate,
				numDataBits: scale.value.dataBits,
				numStopBits: scale.value.stopBits,
				parity: scale.value.parity,
				port: scale.value.path,
			},
		});

		// Register this tab's state up front so the shared message handler updates it too,
		// even when another tab already keeps the connection alive (diff <= 2000).
		const state: ScaleState = {
			isConnected,
			lastMessageTime,
			currentWeight,
		};
		scaleStates.add(state);

		const setActiveScaleInterval = setInterval(() => {
			const ws = getWebSocket();

			const diff = performance.now() - lastMessageTime.value;

			if (diff > 2000 && ws.value?.readyState == WebSocket.OPEN) {
				ws.value?.send(connectionData);
				if (scaleHandler) scaleHandler();

				scaleHandler = createScaleMessageHandler(ws.value, scale.value.path);
			}

			if (tries >= 3 && !hasBridgeIntegration.value) {
				clearInterval(setActiveScaleInterval);
			}
		}, 1000);

		// Cleanup for when this tab closes: stop its interval, unregister its state, and
		// tear the shared handler down only once the last tab is gone.
		return () => {
			clearInterval(setActiveScaleInterval);
			scaleStates.delete(state);

			if (scaleStates.size === 0) {
				if (scaleHandler) scaleHandler();
				scaleHandler = null;
				clearInterval(scaleStatusCheck);
			}
		};
	});
}

function bridgeRequest<T = any>(
	destination: string,
	payload: Record<string, unknown> = {},
	timeoutMs = 10000,
): Promise<T> {
	const ws = getWebSocket().value;
	if (ws == null) return Promise.reject(new Error("WebSocket is not connected"));

	const requestId = crypto.randomUUID();

	return new Promise<T>((resolve, reject) => {
		const cleanup = () => {
			ws.removeEventListener("message", handler);
			clearTimeout(timer);
		};

		const timer = setTimeout(() => {
			cleanup();
			reject(new Error(`Bridge request "${destination}" timed out`));
		}, timeoutMs);

		const handler = (message: MessageEvent) => {
			const wsInput = parseWSString(message.data);
			if ((wsInput?.requestId ?? wsInput?.value?.requestId) !== requestId) return;

			cleanup();
			resolve(wsInput.value);
		};

		ws.addEventListener("message", handler);
		ws.send(JSON.stringify({ destination, requestId, ...payload }));
	});
}

export function selectFileSystemEntry(path: string | null, type: string): Promise<string | null> {
	return bridgeRequest<string | null>("/selectFileSystemEntry", { path, type });
}

export async function openFileSystemEntry(path: string | null) {
	const ws = getWebSocket();

	if (ws.value == null) throw new Error("WebSocket is not connected");
	const requestId = crypto.randomUUID();

	const request = JSON.stringify({
		destination: "/openFileSystemEntry",
		path: path,
		requestId,
	});

	ws.value?.send(request);
	return;
}

export async function updateBridgeIntegration(downloadUrl: string): Promise<string> {
	const previousVersion = currentVersion.value;

	let ack: string | undefined;
	try {
		// The bridge downloads the installer before acking, so allow plenty of time.
		ack = await bridgeRequest<string>("/updateBridge", { downloadUrl }, 120_000);
	} catch (e) {
		// The socket typically drops as the bridge restarts right after acking — not fatal.
		console.warn("updateBridge ack not received (bridge may be restarting):", e);
	}

	// A download failure makes the bridge reply with an error string and stay running (no restart).
	if (ack != null && ack !== "Succeeded") throw new Error(ack);

	// The bridge is restarting: wait for it to reconnect and report the new version. The `open`
	// handler re-runs fetchUpdates() on reconnect, which updates currentVersion.
	return waitForBridgeVersion((v) => v != null && v !== previousVersion, 180_000);
}

function waitForBridgeVersion(predicate: (v: string | null) => boolean, timeoutMs: number): Promise<string> {
	return new Promise((resolve, reject) => {
		if (predicate(currentVersion.value)) {
			resolve(currentVersion.value as string);
			return;
		}
		const timer = setTimeout(() => {
			stop();
			reject(new Error("Bridge did not report the updated version in time"));
		}, timeoutMs);
		const stop = watch(currentVersion, (v) => {
			if (predicate(v)) {
				clearTimeout(timer);
				stop();
				resolve(v as string);
			}
		});
	});
}

export async function fetchUpdates(): Promise<void> {
	const res = await bridgeRequest<{ downloadUrl: string; currentVersion: string; latestVersion: string }>(
		"/fetchUpdates",
	);

	downloadUrl.value = res.downloadUrl;
	currentVersion.value = res.currentVersion;
	latestVersion.value = res.latestVersion;
}
